import React, { useState } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

/**
 * Mehrbetrieb-Komfort: Zeigt nur dann einen Umschalter, wenn dasselbe Konto
 * (gleiche E-Mail) in mehreren Betrieben ein Konto hat. Nutzt switch-business
 * (neues Token ohne Passwort) und lädt die Oberfläche neu, damit alle
 * mandanten-gebundenen Daten zum gewählten Betrieb passen.
 */
export default function BusinessSwitcher() {
  const { switchBusiness } = useAuth();
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (sessionStorage.getItem('business_switched')) {
      sessionStorage.removeItem('business_switched');
      toast.success('Betrieb gewechselt');
    }
  }, []);

  let businesses = [];
  try { businesses = JSON.parse(localStorage.getItem('businesses') || '[]'); } catch { businesses = []; }
  if (!Array.isArray(businesses) || businesses.length < 2) return null;

  const current = businesses.find((b) => b.is_current) || businesses[0];

  const handleSwitch = async (slug) => {
    if (busy || slug === current?.slug) return;
    setBusy(true);
    try {
      await switchBusiness(slug);
      sessionStorage.setItem('business_switched', '1');
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Wechsel fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          disabled={busy}
          data-testid="business-switcher-trigger"
        >
          <Building2 className="h-4 w-4 text-gray-500" />
          <span className="max-w-[160px] truncate">{current?.name || current?.slug}</span>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Betrieb wechseln</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {businesses.map((b) => (
          <DropdownMenuItem
            key={b.tenant_id}
            onClick={() => handleSwitch(b.slug)}
            className="flex items-center justify-between gap-2"
            data-testid={`business-option-${b.slug}`}
          >
            <span className="flex items-center gap-2 min-w-0">
              {b.logo_url
                ? <img src={b.logo_url} alt="" className="h-5 w-5 rounded object-contain" />
                : <span className="h-5 w-5 rounded flex items-center justify-center text-[10px] font-bold text-white" style={{ background: b.primary_color || '#64748b' }}>{(b.name || '?')[0]}</span>}
              <span className="truncate">{b.name || b.slug}</span>
            </span>
            {b.is_current && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
